import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import { getMediaById, getRelated } from "@/mocks/media";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getMediaById(id) : undefined;
  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState("");

  useSeo({
    title: item ? `${item.title} - 兔六影院` : "影片信息不存在 - 兔六影院",
    description: item
      ? `${item.title}（${item.year}）${item.type === "movie" ? "电影" : "电视剧"}，${item.synopsis} 导演：${item.director}，主演：${item.cast.join("、")}。`
      : "影片信息不存在或已下架。",
    keywords: item
      ? `兔六影院,${item.title},${item.genres.join(",")},${item.region}${item.type === "movie" ? "电影" : "电视剧"}`
      : "兔六影院",
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleShare = async () => {
    if (!item) return;
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  if (!item) {
    return (
      <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-24 text-center">
        <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
        <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950">
          影片信息不存在或已下架。
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          该影视作品可能已被移除，看看其他精选内容吧。
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Link
            to="/"
            className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
          <Link
            to="/category/movie"
            className="rounded-md border border-primary-300 px-5 py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50 cursor-pointer whitespace-nowrap"
          >
            浏览电影
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelated(item);
  const fav = isFavorite(item.id);
  const typeLabel = item.type === "movie" ? "电影" : "电视剧";
  const categoryPath = item.type === "movie" ? "/category/movie" : "/category/tv";

  const metaList = [
    { label: "类型", value: item.genres.join(" / ") },
    { label: "年份", value: `${item.year}` },
    { label: "导演", value: item.director },
    { label: "主演", value: item.cast.join("、") },
    { label: "地区", value: item.region },
    { label: "时长/集数", value: item.duration },
  ];

  return (
    <div>
      <div className="relative bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            className="h-full w-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/50"></div>
        </div>

        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-4 md:px-8 py-8 md:py-12">
          <nav className="flex flex-wrap items-center gap-1 text-xs text-primary-200">
            <Link to="/" className="hover:text-accent-400 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={categoryPath} className="hover:text-accent-400 cursor-pointer">
              {typeLabel}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">{item.title}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:gap-10">
            <div className="w-40 shrink-0 self-center sm:w-48 lg:self-start md:w-56">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-primary-700 bg-primary-900">
                <img
                  src={item.poster}
                  alt={`${item.title} 封面`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-md bg-accent-500 px-2 py-1 font-bold text-primary-950">
                  {typeLabel}
                </span>
                <span className="flex items-center gap-1 rounded-md bg-background-50/15 px-2 py-1 font-medium text-background-50">
                  <i className="ri-star-fill text-accent-400"></i>
                  {item.rating.toFixed(1)} 分
                </span>
                <span className="text-primary-200">更新于 {item.updatedAt}</span>
              </div>

              <h1 className="mt-3 font-heading text-2xl font-black text-background-50 md:text-4xl">
                {item.title}
              </h1>

              <div className="mt-4 flex flex-wrap gap-2">
                {item.genres.map((g) => (
                  <Link
                    key={g}
                    to={`${categoryPath}?genre=${encodeURIComponent(g)}`}
                    className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 hover:bg-secondary-200 cursor-pointer"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-100 md:text-base">
                {item.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => toggle(item.id)}
                  className={`flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    fav
                      ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                      : "border border-background-50/40 text-background-50 hover:bg-background-50/10"
                  }`}
                >
                  <i className={fav ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {fav ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center gap-2 rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line"></i>
                  分享链接
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              剧情简介
            </h2>
            <p className="mt-3 text-sm leading-loose text-foreground-700 md:text-base">
              {item.story}
            </p>

            <h2 className="mt-8 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              相关推荐
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              根据类型与导演为你推荐的{typeLabel}作品
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {related.map((r) => (
                <MediaCard key={r.id} item={r} showSynopsis={false} />
              ))}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                影片信息
              </h2>
              <dl className="mt-4 space-y-3">
                {metaList.map((m) => (
                  <div key={m.label} className="flex gap-3 text-sm">
                    <dt className="w-20 shrink-0 text-foreground-500">{m.label}</dt>
                    <dd className="flex-1 text-foreground-800">{m.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-sm text-foreground-600">所属分类</p>
                <Link
                  to={categoryPath}
                  className="mt-2 inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-folder-2-line"></i>
                  进入{typeLabel}频道
                </Link>
              </div>

              <p className="mt-5 text-xs leading-relaxed text-foreground-500">
                本站仅提供影视作品信息介绍，不提供内容存储与在线播放服务。
                影片资料均为演示数据。
              </p>
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-md bg-primary-900 px-5 py-3 text-sm text-background-50 shadow-sm">
          {toast}
        </div>
      )}
    </div>
  );
}